/**
 * Database Settings Page
 * Allows switching between local and production databases
 */

import { useState } from 'react';
import { useGetDatabaseStatusQuery, useSwitchDatabaseMutation, useLazyCompareSchemasQuery } from '../store/api/databaseApi';

export default function DatabaseSettings() {
  const { data: status, isLoading, error, refetch } = useGetDatabaseStatusQuery();
  const [switchDatabase, { isLoading: isSwitching }] = useSwitchDatabaseMutation();
  const [triggerCompare, { data: comparisonData, isLoading: isComparing, error: compareError }] = useLazyCompareSchemasQuery();
  const [showRestartInstructions, setShowRestartInstructions] = useState(false);
  const [restartInstructions, setRestartInstructions] = useState<any>(null);
  const [showComparison, setShowComparison] = useState(false);

  const handleSwitch = async (environment: 'local' | 'production') => {
    if (window.confirm(`Are you sure you want to switch to ${environment} database? This will require restarting all services.`)) {
      try {
        const result = await switchDatabase({ environment }).unwrap();
        
        if (result.data.requiresRestart) {
          setRestartInstructions(result.data.restartInstructions);
          setShowRestartInstructions(true);
        }
        
        // Refetch status after a short delay
        setTimeout(() => {
          refetch();
        }, 1000);
        
        alert(result.message);
      } catch (err: any) {
        alert(`Error: ${err.data?.message || 'Failed to switch database'}`);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading database status...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold mb-2">Error Loading Database Status</h3>
          <p className="text-red-600">Failed to load database configuration. Please check the API connection.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Database Settings</h1>
        <p className="text-gray-600 mt-2">Switch between local and production databases</p>
      </div>

      {/* Current Environment Banner */}
      <div className={`mb-6 p-4 rounded-lg border-2 ${
        status?.currentEnvironment === 'production' 
          ? 'bg-red-50 border-red-300' 
          : 'bg-blue-50 border-blue-300'
      }`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">
              Current Environment: 
              <span className={`ml-2 ${
                status?.currentEnvironment === 'production' 
                  ? 'text-red-700' 
                  : 'text-blue-700'
              }`}>
                {status?.currentEnvironment?.toUpperCase()}
              </span>
            </h2>
            {status?.currentEnvironment === 'production' && (
              <p className="text-red-600 text-sm mt-1">
                ⚠️ You are connected to the PRODUCTION database. Be careful with any changes!
              </p>
            )}
          </div>
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full ${
              status?.currentEnvironment === 'production' 
                ? 'bg-red-500' 
                : 'bg-blue-500'
            } animate-pulse`}></div>
          </div>
        </div>
      </div>

      {/* Restart Instructions Modal */}
      {showRestartInstructions && restartInstructions && (
        <div className="mb-6 bg-yellow-50 border-2 border-yellow-400 rounded-lg p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-lg font-semibold text-yellow-800 mb-3">
                ⚠️ RESTART REQUIRED - Follow These Steps:
              </h3>
              <div className="space-y-3">
                <div className="bg-white rounded p-3 border border-yellow-200">
                  <h4 className="font-semibold text-gray-900 mb-1">1. API Server</h4>
                  <p className="text-sm text-gray-700">{restartInstructions.api}</p>
                  <code className="block mt-2 bg-gray-100 p-2 rounded text-xs">
                    cd C:\motionode_code\motionode_api && npm start
                  </code>
                </div>
                <div className="bg-white rounded p-3 border border-yellow-200">
                  <h4 className="font-semibold text-gray-900 mb-1">2. Admin Backend</h4>
                  <p className="text-sm text-gray-700">{restartInstructions.admin}</p>
                  <code className="block mt-2 bg-gray-100 p-2 rounded text-xs">
                    cd C:\motionode_code\motionode_admin\back && npm run dev
                  </code>
                </div>
                <div className="bg-white rounded p-3 border border-yellow-200">
                  <h4 className="font-semibold text-gray-900 mb-1">3. Web Frontend (if needed)</h4>
                  <p className="text-sm text-gray-700">{restartInstructions.web}</p>
                  <code className="block mt-2 bg-gray-100 p-2 rounded text-xs">
                    cd C:\motionode_code\motionode_web && npm start
                  </code>
                </div>
              </div>
              <button
                onClick={() => setShowRestartInstructions(false)}
                className="mt-4 px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 transition"
              >
                I understand, dismiss this message
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Database Configurations */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Local Database */}
        <div className={`border-2 rounded-lg p-6 transition ${
          status?.currentEnvironment === 'local'
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-200 bg-white'
        }`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-900">Local Database</h3>
            {status?.currentEnvironment === 'local' && (
              <span className="px-3 py-1 bg-blue-500 text-white text-xs font-semibold rounded-full">
                ACTIVE
              </span>
            )}
          </div>
          
          {status?.configurations.local ? (
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Host:</span>
                <span className="font-mono text-gray-900">{status.configurations.local.host}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Database:</span>
                <span className="font-mono text-gray-900">{status.configurations.local.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">User:</span>
                <span className="font-mono text-gray-900">{status.configurations.local.user}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Dialect:</span>
                <span className="font-mono text-gray-900">{status.configurations.local.dialect}</span>
              </div>
            </div>
          ) : (
            <div className="text-red-600 text-sm mb-4">
              ⚠️ Local database not configured
            </div>
          )}

          <button
            onClick={() => handleSwitch('local')}
            disabled={status?.currentEnvironment === 'local' || isSwitching || !status?.configurations.local}
            className={`w-full py-2 px-4 rounded font-semibold transition ${
              status?.currentEnvironment === 'local'
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            } ${isSwitching ? 'opacity-50 cursor-wait' : ''}`}
          >
            {status?.currentEnvironment === 'local' ? 'Currently Active' : 'Switch to Local'}
          </button>
        </div>

        {/* Production Database */}
        <div className={`border-2 rounded-lg p-6 transition ${
          status?.currentEnvironment === 'production'
            ? 'border-red-500 bg-red-50'
            : 'border-gray-200 bg-white'
        }`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-900">Production Database</h3>
            {status?.currentEnvironment === 'production' && (
              <span className="px-3 py-1 bg-red-500 text-white text-xs font-semibold rounded-full">
                ACTIVE
              </span>
            )}
          </div>
          
          {status?.configurations.production ? (
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Host:</span>
                <span className="font-mono text-gray-900">{status.configurations.production.host}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Database:</span>
                <span className="font-mono text-gray-900">{status.configurations.production.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">User:</span>
                <span className="font-mono text-gray-900">{status.configurations.production.user}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Dialect:</span>
                <span className="font-mono text-gray-900">{status.configurations.production.dialect}</span>
              </div>
            </div>
          ) : (
            <div className="text-red-600 text-sm mb-4">
              ⚠️ Production database not configured
            </div>
          )}

          <button
            onClick={() => handleSwitch('production')}
            disabled={status?.currentEnvironment === 'production' || isSwitching || !status?.configurations.production}
            className={`w-full py-2 px-4 rounded font-semibold transition ${
              status?.currentEnvironment === 'production'
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-red-600 text-white hover:bg-red-700'
            } ${isSwitching ? 'opacity-50 cursor-wait' : ''}`}
          >
            {status?.currentEnvironment === 'production' ? 'Currently Active' : 'Switch to Production'}
          </button>
        </div>
      </div>

      {/* Schema Comparison Section */}
      <div className="mt-8 border-t pt-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Schema Comparison</h2>
            <p className="text-gray-600 mt-1">Compare database schemas between local and production to identify missing tables, columns, triggers, etc.</p>
          </div>
          <button
            onClick={() => {
              setShowComparison(true);
              triggerCompare();
            }}
            disabled={isComparing}
            className={`px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition ${
              isComparing ? 'opacity-50 cursor-wait' : ''
            }`}
          >
            {isComparing ? 'Analyzing...' : 'Compare Schemas'}
          </button>
        </div>

        {compareError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <h3 className="text-red-800 font-semibold mb-2">Error During Comparison</h3>
            <p className="text-red-600 text-sm">Failed to compare schemas. Please check database connections.</p>
          </div>
        )}

        {showComparison && comparisonData && (
          <div className="space-y-6">
            {/* Summary */}
            <div className={`p-4 rounded-lg border-2 ${
              comparisonData.summary.hasDifferences 
                ? 'bg-yellow-50 border-yellow-300' 
                : 'bg-green-50 border-green-300'
            }`}>
              <div className="flex items-center">
                {comparisonData.summary.hasDifferences ? (
                  <>
                    <svg className="h-6 w-6 text-yellow-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <h3 className="text-lg font-semibold text-yellow-800">
                        {comparisonData.summary.totalDifferences} Difference(s) Found
                      </h3>
                      <p className="text-yellow-700 text-sm">The databases have schema differences that need attention.</p>
                    </div>
                  </>
                ) : (
                  <>
                    <svg className="h-6 w-6 text-green-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <h3 className="text-lg font-semibold text-green-800">Schemas Match</h3>
                      <p className="text-green-700 text-sm">Local and production databases have identical schemas.</p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Tables Missing */}
            {(comparisonData.comparison.tablesOnlyInLocal.length > 0 || comparisonData.comparison.tablesOnlyInProduction.length > 0) && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">📋 Missing Tables</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {comparisonData.comparison.tablesOnlyInLocal.length > 0 && (
                    <div className="bg-blue-50 rounded p-4">
                      <h4 className="font-semibold text-blue-900 mb-2">Only in Local ({comparisonData.comparison.tablesOnlyInLocal.length})</h4>
                      <ul className="space-y-1 text-sm text-blue-800">
                        {comparisonData.comparison.tablesOnlyInLocal.map(table => (
                          <li key={table} className="font-mono">• {table}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {comparisonData.comparison.tablesOnlyInProduction.length > 0 && (
                    <div className="bg-red-50 rounded p-4">
                      <h4 className="font-semibold text-red-900 mb-2">Only in Production ({comparisonData.comparison.tablesOnlyInProduction.length})</h4>
                      <ul className="space-y-1 text-sm text-red-800">
                        {comparisonData.comparison.tablesOnlyInProduction.map(table => (
                          <li key={table} className="font-mono">• {table}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Column Differences */}
            {comparisonData.comparison.columnDifferences.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">🔧 Column Differences ({comparisonData.comparison.columnDifferences.length})</h3>
                <div className="space-y-3">
                  {comparisonData.comparison.columnDifferences.map((diff, idx) => (
                    <div key={idx} className="border-l-4 border-yellow-400 pl-4 py-2 bg-yellow-50">
                      <div className="font-semibold text-gray-900">
                        {diff.table}.{diff.column}
                      </div>
                      <div className="text-sm text-gray-700 mt-1">
                        {diff.inLocal && diff.inProduction ? (
                          <>
                            <div className="text-blue-700">Local: <span className="font-mono">{diff.localDefinition}</span></div>
                            <div className="text-red-700">Production: <span className="font-mono">{diff.productionDefinition}</span></div>
                          </>
                        ) : diff.inLocal ? (
                          <div className="text-blue-700">Only in Local: <span className="font-mono">{diff.localDefinition}</span></div>
                        ) : (
                          <div className="text-red-700">Only in Production: <span className="font-mono">{diff.productionDefinition}</span></div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Index Differences */}
            {comparisonData.comparison.indexDifferences.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">🔍 Index Differences ({comparisonData.comparison.indexDifferences.length})</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {comparisonData.comparison.indexDifferences.filter(d => d.inLocal && !d.inProduction).length > 0 && (
                    <div className="bg-blue-50 rounded p-4">
                      <h4 className="font-semibold text-blue-900 mb-2">Only in Local</h4>
                      <ul className="space-y-1 text-sm text-blue-800">
                        {comparisonData.comparison.indexDifferences
                          .filter(d => d.inLocal && !d.inProduction)
                          .map((diff, idx) => (
                            <li key={idx} className="font-mono">• {diff.table}.{diff.index}</li>
                          ))}
                      </ul>
                    </div>
                  )}
                  {comparisonData.comparison.indexDifferences.filter(d => !d.inLocal && d.inProduction).length > 0 && (
                    <div className="bg-red-50 rounded p-4">
                      <h4 className="font-semibold text-red-900 mb-2">Only in Production</h4>
                      <ul className="space-y-1 text-sm text-red-800">
                        {comparisonData.comparison.indexDifferences
                          .filter(d => !d.inLocal && d.inProduction)
                          .map((diff, idx) => (
                            <li key={idx} className="font-mono">• {diff.table}.{diff.index}</li>
                          ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Foreign Key Differences */}
            {comparisonData.comparison.foreignKeyDifferences.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">🔗 Foreign Key Differences ({comparisonData.comparison.foreignKeyDifferences.length})</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {comparisonData.comparison.foreignKeyDifferences.filter(d => d.inLocal && !d.inProduction).length > 0 && (
                    <div className="bg-blue-50 rounded p-4">
                      <h4 className="font-semibold text-blue-900 mb-2">Only in Local</h4>
                      <ul className="space-y-1 text-sm text-blue-800">
                        {comparisonData.comparison.foreignKeyDifferences
                          .filter(d => d.inLocal && !d.inProduction)
                          .map((diff, idx) => (
                            <li key={idx} className="font-mono">• {diff.table}.{diff.foreignKey}</li>
                          ))}
                      </ul>
                    </div>
                  )}
                  {comparisonData.comparison.foreignKeyDifferences.filter(d => !d.inLocal && d.inProduction).length > 0 && (
                    <div className="bg-red-50 rounded p-4">
                      <h4 className="font-semibold text-red-900 mb-2">Only in Production</h4>
                      <ul className="space-y-1 text-sm text-red-800">
                        {comparisonData.comparison.foreignKeyDifferences
                          .filter(d => !d.inLocal && d.inProduction)
                          .map((diff, idx) => (
                            <li key={idx} className="font-mono">• {diff.table}.{diff.foreignKey}</li>
                          ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Trigger Differences */}
            {(comparisonData.comparison.triggersOnlyInLocal.length > 0 || comparisonData.comparison.triggersOnlyInProduction.length > 0) && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">⚡ Trigger Differences</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {comparisonData.comparison.triggersOnlyInLocal.length > 0 && (
                    <div className="bg-blue-50 rounded p-4">
                      <h4 className="font-semibold text-blue-900 mb-2">Only in Local ({comparisonData.comparison.triggersOnlyInLocal.length})</h4>
                      <ul className="space-y-1 text-sm text-blue-800">
                        {comparisonData.comparison.triggersOnlyInLocal.map(trigger => (
                          <li key={trigger} className="font-mono">• {trigger}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {comparisonData.comparison.triggersOnlyInProduction.length > 0 && (
                    <div className="bg-red-50 rounded p-4">
                      <h4 className="font-semibold text-red-900 mb-2">Only in Production ({comparisonData.comparison.triggersOnlyInProduction.length})</h4>
                      <ul className="space-y-1 text-sm text-red-800">
                        {comparisonData.comparison.triggersOnlyInProduction.map(trigger => (
                          <li key={trigger} className="font-mono">• {trigger}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* View Differences */}
            {(comparisonData.comparison.viewsOnlyInLocal.length > 0 || comparisonData.comparison.viewsOnlyInProduction.length > 0) && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">👁️ View Differences</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {comparisonData.comparison.viewsOnlyInLocal.length > 0 && (
                    <div className="bg-blue-50 rounded p-4">
                      <h4 className="font-semibold text-blue-900 mb-2">Only in Local ({comparisonData.comparison.viewsOnlyInLocal.length})</h4>
                      <ul className="space-y-1 text-sm text-blue-800">
                        {comparisonData.comparison.viewsOnlyInLocal.map(view => (
                          <li key={view} className="font-mono">• {view}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {comparisonData.comparison.viewsOnlyInProduction.length > 0 && (
                    <div className="bg-red-50 rounded p-4">
                      <h4 className="font-semibold text-red-900 mb-2">Only in Production ({comparisonData.comparison.viewsOnlyInProduction.length})</h4>
                      <ul className="space-y-1 text-sm text-red-800">
                        {comparisonData.comparison.viewsOnlyInProduction.map(view => (
                          <li key={view} className="font-mono">• {view}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Warning Section */}
      <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h3 className="font-semibold text-gray-900 mb-2">⚠️ Important Notes:</h3>
        <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
          <li>Switching databases requires restarting the API server, admin backend, and potentially the web frontend</li>
          <li>All active connections will be terminated during the switch</li>
          <li>Make sure you have the necessary permissions before switching to production</li>
          <li>Always backup important data before making changes in production</li>
        </ul>
      </div>
    </div>
  );
}

